import React, {useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {getReferral, getFeaturedContestBySlug, getMyState} from '@/api/participant.js';

// Page de partage du parrain (ouverte depuis le bouton "Partager à un ami" de l'email d'invitation).
// Objectif : ne PAS se limiter à WhatsApp. Sur mobile, le bouton "Partager" ouvre la feuille de partage
// native de l'OS (navigator.share) → le parrain choisit l'appli qu'il veut. WhatsApp (canal dominant au
// bar) et "copier le lien" restent affichés en permanence, y compris avec Web Share — trois chemins voulus.
// `demoData` (optional) drives a standalone demo (route /demo-invite) with mock data and no API call.
export const ReferralSharePage = ({demoData = null}) => {
    const params = useParams();
    const {t} = useTranslation();

    const code = demoData?.code ?? params.code;

    const [loading, setLoading] = useState(!demoData);
    const [referral, setReferral] = useState(demoData?.referral ?? null);
    const [coverUrl, setCoverUrl] = useState(demoData?.coverUrl ?? null);
    // Whether the referrer themselves already joined the contest (hide the "play" CTA if so).
    const [hasPlayed, setHasPlayed] = useState(demoData?.hasPlayed ?? false);
    const [copied, setCopied] = useState(false);
    // Resolution failed to LOAD (offline, timeout) — very different from a code resolved as invalid:
    // telling a referrer on flaky bar wifi that their link "n'est plus actif" would be false and damaging.
    const [loadFailed, setLoadFailed] = useState(false);
    const [retryTick, setRetryTick] = useState(0);

    useEffect(() => {
        if (demoData) return; // demo mode: no backend call
        let alive = true;
        setLoading(true);
        setLoadFailed(false);
        // The code is the source of truth for the org: resolve it first, THEN use its slug for the
        // featured-contest fetch (cover + the referrer's played state). No slug is trusted from the URL.
        getReferral(code).then((res) => {
            if (!alive) return;
            // getReferral swallows network errors into null; the endpoint itself always answers 200.
            if (!res) {
                setLoadFailed(true);
                setLoading(false);
                return;
            }
            setReferral(res);
            setLoading(false);
            const orgSlug = res?.organizationSlug;
            if (!orgSlug) return;
            getFeaturedContestBySlug(orgSlug).then((contest) => {
                if (!alive || !contest) return;
                setCoverUrl(contest.coverUrl || null);
                if (contest.id) {
                    getMyState(contest.id).then(({data}) => {
                        if (alive && data?.status) setHasPlayed(String(data.status).startsWith('JOINED'));
                    });
                }
            }).catch(() => { /* pas de contest featured → pas de cover, on garde le fond uni */ });
        });
        return () => { alive = false; };
    }, [code, demoData, retryTick]);

    // Org slug comes from the resolved code, never from the URL.
    const slug = referral?.organizationSlug;
    // Le lien que le parrain partage = la landing du filleul, qui porte son code.
    const refLink = `${window.location.origin}/${slug}?ref=${code}`;
    // One reward PER SIDE: the referrer sees their own gift and their friend's (they may differ).
    const referrerReward = referral?.referrerRewardTitle;
    const refereeReward = referral?.refereeRewardTitle;
    const orgName = referral?.organizationName;
    const orgLogo = referral?.organizationLogoUrl;
    const cap = referral?.referrerCap;
    const used = referral?.referralsUsed;
    const reason = referral?.reason;
    // Le message parle AU futur filleul : on lui promet SON cadeau (pas celui du parrain).
    const message = t('referral.share_message', {
        reward: refereeReward || t('referral.welcome_reward', 'une récompense'),
        org: orgName || t('referral.share_default_venue', 'mon bar'),
    });
    const canNativeShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

    const nativeShare = async () => {
        try {
            // Web Share d'iOS est incohérent selon la cible : sans `url` la feuille dégénère en feuille
            // "texte" (ne propose que Mail/Notes), mais avec `url` certaines cibles (Notes, Mail) gardent
            // l'url et JETTENT le `text` → l'accroche disparaît. On blinde : le lien vit DANS le texte
            // (jamais perdu, quelle que soit la cible) ET on passe `url` (feuille "lien" riche, WhatsApp
            // visible). Une éventuelle duplication du lien sur certaines cibles est un moindre mal vs un
            // partage sans lien ou sans accroche.
            await navigator.share({title: t('referral.share_page_title', 'Invite un ami'), text: `${message}\n${refLink}`, url: refLink});
        } catch { /* l'utilisateur a annulé la feuille de partage — rien à faire */ }
    };

    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(`${message} ${refLink}`)}`;

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(refLink);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch { /* clipboard indisponible — le lien reste affiché en clair */ }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-brand-dark flex items-center justify-center">
                <span className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin"/>
            </div>
        );
    }

    // Échec réseau : le lien est peut-être parfaitement valide — on propose de réessayer, on n'affirme rien.
    if (loadFailed) {
        return (
            <div className="min-h-screen bg-brand-dark flex flex-col items-center justify-center px-6 text-center">
                <span className="text-5xl mb-4">📶</span>
                <h1 className="text-2xl font-black text-white mb-2">
                    {t('referral.share_error_title', 'Connexion impossible')}
                </h1>
                <p className="text-white/50 font-medium mb-6">
                    {t('referral.share_error_text', 'Impossible de charger ton lien. Vérifie ta connexion.')}
                </p>
                <button
                    onClick={() => setRetryTick((n) => n + 1)}
                    className="px-6 py-3 bg-action-green text-white font-black rounded-xl text-sm uppercase tracking-wide"
                >
                    {t('referral.share_error_retry', 'Réessayer')}
                </button>
            </div>
        );
    }

    // Cap atteint : le parrain a amené tous ses amis → écran "complet" positif (pas d'erreur).
    if (reason === 'CAP_REACHED') {
        return (
            <div className="min-h-screen bg-brand-dark flex flex-col items-center justify-center px-6 text-center">
                <span className="text-6xl mb-4">🎉</span>
                <h1 className="text-3xl font-black text-white uppercase mb-2">
                    {t('referral.share_complete_title', "C'est complet, bravo !")}
                </h1>
                <p className="text-white/60 font-medium max-w-xs">
                    {t('referral.share_complete_text', 'Tu as parrainé tes {{cap}} amis. Merci !', {cap: cap ?? 3})}
                </p>
                {orgName && (
                    <a href={`/${slug}`} className="mt-8 text-yellow-400 font-bold text-sm underline underline-offset-4">
                        {t('referral.share_play_cta', 'Jouer au prochain match')} →
                    </a>
                )}
            </div>
        );
    }

    // Programme terminé / budget global épuisé.
    if (reason === 'EXPIRED' || reason === 'BUDGET_EXHAUSTED') {
        return (
            <div className="min-h-screen bg-brand-dark flex flex-col items-center justify-center px-6 text-center">
                <span className="text-5xl mb-4">⌛</span>
                <h1 className="text-2xl font-black text-white mb-2">
                    {t('referral.share_ended_title', 'Offre terminée')}
                </h1>
                <p className="text-white/50 font-medium">
                    {t('referral.share_ended_text', "Cette offre de parrainage n'est plus active.")}
                </p>
            </div>
        );
    }

    // Code inconnu / invalide : on ne promet rien, message honnête.
    if (!referral?.valid) {
        return (
            <div className="min-h-screen bg-brand-dark flex flex-col items-center justify-center px-6 text-center">
                <span className="text-5xl mb-4">🙈</span>
                <h1 className="text-2xl font-black text-white mb-2">
                    {t('referral.share_invalid_title', 'Lien introuvable')}
                </h1>
                <p className="text-white/50 font-medium">
                    {t('referral.share_invalid_text', "Ce lien de parrainage n'est plus actif.")}
                </p>
            </div>
        );
    }

    return (
        <div className="relative min-h-screen bg-brand-dark flex flex-col items-center justify-between px-6 py-10 select-none overflow-hidden">
            {/* Fond immersif (même traitement que StepWelcome) quand un cover de contest est dispo. */}
            {coverUrl && (
                <div className="absolute inset-0 -z-0">
                    <img src={coverUrl} alt="" className="w-full h-full object-cover object-top filter brightness-[0.35] grayscale-20"/>
                    <div className="absolute inset-0 bg-linear-to-t from-brand-dark via-brand-dark/85 to-brand-dark/40"/>
                </div>
            )}

            <div className="relative z-10 w-full flex flex-col items-center justify-between flex-1">
                {/* En-tête co-brandé GOJO × établissement : on voit tout de suite de quel bar il s'agit. */}
                <div className="pt-4 flex items-center gap-3">
                    <h1 className="text-2xl font-black text-white tracking-tight">
                        GOJO<span className="text-yellow-400">.</span>
                    </h1>
                    {(orgLogo || orgName) && (
                        <>
                            <span className="text-white/30 text-lg">✕</span>
                            {orgLogo
                                ? <img src={orgLogo} alt={orgName || ''} className="w-9 h-9 rounded-full object-cover bg-white/10 border border-white/15"/>
                                : <span className="text-sm font-black text-white uppercase tracking-wide">{orgName}</span>}
                        </>
                    )}
                </div>

                <div className="w-full max-w-sm text-center">
                    <div className="text-5xl mb-5">🤝</div>
                    <h2 className="text-3xl font-black text-white uppercase leading-tight mb-2">
                        {t('referral.share_page_title', 'Invite un ami')}
                    </h2>
                    {orgName && (
                        <p className="text-yellow-400 font-black uppercase tracking-widest text-xs mb-3">
                            {t('referral.share_at_venue', 'chez {{org}}', {org: orgName})}
                        </p>
                    )}
                    <p className="text-white/70 font-medium mb-5 leading-relaxed">
                        {t('referral.share_page_subtitle', 'Partage ton lien : vous gagnez chacun votre récompense.')}
                    </p>

                    {/* Progression : combien d'amis déjà parrainés sur le cap (ex. 1/3). */}
                    {cap != null && (
                        <div className="inline-flex items-center gap-2.5 mb-6 px-4 py-2 rounded-full bg-white/5 border border-white/10 backdrop-blur-sm">
                            <div className="flex gap-1">
                                {Array.from({length: cap}).map((_, i) => (
                                    <span key={i} className={`w-2 h-2 rounded-full ${i < (used ?? 0) ? 'bg-action-green' : 'bg-white/20'}`}/>
                                ))}
                            </div>
                            <span className="text-white/70 text-xs font-black">
                                {t('referral.share_progress', '{{used}}/{{cap}} amis invités', {used: used ?? 0, cap})}
                            </span>
                        </div>
                    )}

                    {/* Les récompenses = les offres configurées par l'établissement (une par côté), jamais un
                        texte en dur. Deux slots — même mise en scène que l'email d'invitation. */}
                    <div className="rounded-2xl bg-white/5 border border-yellow-400/25 px-4 py-4 backdrop-blur-sm">
                        <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2">
                            <div>
                                <p className="text-[10px] font-black text-yellow-400 uppercase tracking-widest mb-1.5">
                                    {t('referral.share_reward_you', 'Pour toi')}
                                </p>
                                <p className="text-base font-black text-white leading-tight">
                                    {referrerReward || t('referral.welcome_reward', 'Une récompense offerte')}
                                </p>
                            </div>
                            <span className="text-yellow-400 text-xl font-black">+</span>
                            <div>
                                <p className="text-[10px] font-black text-yellow-400 uppercase tracking-widest mb-1.5">
                                    {t('referral.share_reward_friend', 'Pour ton ami')}
                                </p>
                                <p className="text-base font-black text-white leading-tight">
                                    {refereeReward || t('referral.welcome_reward', 'Une récompense offerte')}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="w-full max-w-sm space-y-3 pt-6">
                    {/* WhatsApp = canal dominant au bar ET message 100% maîtrisé (texte + lien dans le corps
                        wa.me, quel que soit l'OS) → action PRINCIPALE. Le partage natif ci-dessous est à la
                        merci de la feuille de l'OS (macOS/iOS peuvent ne garder que le lien), donc secondaire. */}
                    <a
                        href={whatsappUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block w-full py-4 text-center font-black rounded-xl text-base uppercase tracking-wide transition-transform active:scale-98 shadow-lg hover:brightness-110"
                        style={{backgroundColor: '#25D366', color: '#0F172A'}}
                    >
                        {t('referral.share_whatsapp', 'WhatsApp')}
                    </a>

                    {canNativeShare && (
                        <button
                            onClick={nativeShare}
                            className="w-full py-3.5 border-2 border-white/15 text-white/80 font-bold rounded-xl text-sm hover:border-white/30 transition-colors"
                        >
                            {t('referral.share_cta', 'Autre appli')} 📲
                        </button>
                    )}

                    <button
                        onClick={copyLink}
                        className="w-full py-3.5 border-2 border-white/15 text-white/80 font-bold rounded-xl text-sm hover:border-white/30 transition-colors"
                    >
                        {copied
                            ? t('referral.share_copied', 'Lien copié !')
                            : t('referral.share_copy', 'Copier le lien')}
                    </button>

                    {/* Le parrain n'a pas encore joué → on lui propose de jouer le match lui aussi. */}
                    {!hasPlayed && (
                        <a
                            href={`/${slug}`}
                            className="block w-full pt-3 text-center text-white/60 hover:text-white font-bold text-sm underline underline-offset-4 transition-colors"
                        >
                            {t('referral.share_play_cta', 'Jouer au prochain match')} →
                        </a>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ReferralSharePage;
